import React, { useState, useMemo, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { useThemeStore } from '@/stores';
import { useCustomers } from '@/hooks/useData';
import {
  debtsService,
  Debt,
  DebtPayment,
  DebtLog,
  DebtAttachment
} from '@/services/debts';
import {
  Coins,
  Receipt,
  Search,
  Plus,
  Edit,
  Trash2,
  RotateCcw,
  FileText,
  CheckCircle,
  AlertCircle,
  Calendar,
  ArrowUpDown,
  User,
  Download,
  LayoutDashboard,
  Bell,
  Eye,
  Clock,
  ArrowLeft,
  ArrowRight,
  History,
  FileSpreadsheet,
  X,
  CreditCard,
  Paperclip,
  Check,
  Filter,
  DollarSign
} from 'lucide-react';

const paymentMethodLabels: Record<string, string> = {
  CASH: 'نقدًا',
  CARD: 'بطاقة ائتمان',
  TRANSFER: 'تحويل إلكتروني',
  CHEQUE: 'شيك مصرفي',
};

const statusLabels: Record<string, string> = {
  ACTIVE: 'نشط',
  PAID: 'مسدد بالكامل',
  OVERDUE: 'متأخر',
};

const demoCustomers: any[] = [];

const unwrapResponse = (res: any): any => {
  if (!res) return null;
  let data = res;
  while (data && typeof data === 'object' && 'data' in data && !('model' in data) && !('items' in data)) {
    data = data.data;
  }
  return data;
};

const DebtsPage: React.FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { theme } = useThemeStore();
  const isLight = theme === 'light';

  // Fetch real customers list for debts
  const { data: rawCustomersData } = useCustomers({ pageSize: 1000, pageNumber: 1 });

  const allCustomers = useMemo(() => {
    let items: any[] = [];
    if (rawCustomersData) {
      const unwrapped = unwrapResponse(rawCustomersData);
      items = (unwrapped as any)?.items || (unwrapped as any)?.data || [];
    }

    const formatted = Array.isArray(items) ? items.map((item: any) => ({
      id: item.self?.id || '',
      name: item.self?.displayValue?.trim() || '',
      phone: item.primaryContact?.mobile || item.primaryContact?.secondaryPhone || '—',
    })).filter(c => c.id && c.name) : [];

    const merged = [...demoCustomers];
    formatted.forEach((sysCust: any) => {
      if (!merged.some(m => m.id === sysCust.id)) {
        merged.push(sysCust);
      }
    });
    return merged;
  }, [rawCustomersData]);

  const [subscriberType, setSubscriberType] = useState<'system' | 'manual'>('system');
  const [customerSearchQuery, setCustomerSearchQuery] = useState('');
  const [isCustomerDropdownOpen, setIsCustomerDropdownOpen] = useState(false);

  useEffect(() => {
    const clickOutside = () => {
      setIsCustomerDropdownOpen(false);
    };
    document.addEventListener('click', clickOutside);
    return () => document.removeEventListener('click', clickOutside);
  }, []);

  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const [deleteConfirm, setDeleteConfirm] = useState<{
    isOpen: boolean;
    type: 'DEBT' | 'PAYMENT';
    id: string;
    extraId?: string;
    message: string;
  }>({
    isOpen: false,
    type: 'DEBT',
    id: '',
    message: '',
  });

  // Active Tab
  const [activeTab, setActiveTab] = useState<'dashboard' | 'list' | 'alerts' | 'logs'>('dashboard');

  // Trigger filter by subscriber if navigated from Customers page
  const preSelectedCustomerId = location.state?.customerId;
  const preSelectedCustomerName = location.state?.customerName;
  const preSelectedCustomerPhone = location.state?.customerPhone;

  // Local State for data
  const [debts, setDebts] = useState<Debt[]>([]);
  const [payments, setPayments] = useState<DebtPayment[]>([]);
  const [logs, setLogs] = useState<DebtLog[]>([]);

  // Filtering & Pagination State
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [customerFilter, setCustomerFilter] = useState(preSelectedCustomerId || '');
  const [typeFilter, setTypeFilter] = useState('ALL'); // ALL, INSTALLMENT, ONETIME
  const [showDeleted, setShowDeleted] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [sortField, setSortField] = useState<keyof Debt>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  // Modals visibility
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isDetailsModalOpen, setIsDetailsModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const [selectedDebt, setSelectedDebt] = useState<Debt | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<DebtPayment | null>(null);

  // Form states
  const [formData, setFormData] = useState({
    subscriber_id: '',
    manual_name: '',
    manual_phone: '',
    amount: '',
    reason: '',
    due_date: '',
    notes: '',
    is_installment: false,
    attachments: [] as DebtAttachment[],
  });

  const [paymentFormData, setPaymentFormData] = useState({
    amount: '',
    payment_method: 'CASH' as DebtPayment['payment_method'],
    receipt_number: '',
    notes: '',
  });

  const [fileUploadSim, setFileUploadSim] = useState<string | null>(null);

  // Fetch data async from Supabase (or localStorage fallback)
  const refreshData = async () => {
    await debtsService.warmCache();
    const allDebts = await debtsService.getDebts(true);
    const allPayments = await debtsService.getPayments();
    const allLogs = await debtsService.getLogs();
    setDebts(allDebts);
    setPayments(allPayments);
    setLogs(allLogs);
  };

  useEffect(() => {
    refreshData();
  }, []);

  // Handle subscriber selection pre-filter when router state changes
  useEffect(() => {
    if (preSelectedCustomerId) {
      setCustomerFilter(preSelectedCustomerId);
      setActiveTab('list');

      // Populate dropdown search input query and choose system type
      if (preSelectedCustomerName) {
        setCustomerSearchQuery(`${preSelectedCustomerName} (${preSelectedCustomerId})`);
      } else {
        const match = allCustomers.find(c => String(c.id) === String(preSelectedCustomerId));
        if (match) {
          setCustomerSearchQuery(`${match.name} (${match.id})`);
        }
      }
      setSubscriberType('system');

      // If requested to open the add modal directly
      if (location.state?.openAddModal) {
        setFormData({
          subscriber_id: preSelectedCustomerId,
          manual_name: '',
          manual_phone: '',
          amount: '',
          reason: '',
          due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
          notes: '',
          is_installment: false,
          attachments: [],
        });
        setIsAddModalOpen(true);
        // Clear openAddModal state to avoid resetting form data when allCustomers changes
        navigate(location.pathname, {
          replace: true,
          state: {
            ...location.state,
            openAddModal: false,
          }
        });
      }
    }
  }, [preSelectedCustomerId, preSelectedCustomerName, preSelectedCustomerPhone, allCustomers, location.state, navigate]);

  // Derived dashboard stats
  const stats = useMemo(() => debtsService.getDashboardStats(), [debts]);
  const smartAlerts = useMemo(() => debtsService.getSmartAlerts(), [debts]);

  // Sort & Filter debts
  const processedDebts = useMemo(() => {
    let result = debts.filter(d => {
      // Soft delete check
      if (showDeleted) {
        return d.deleted_at !== null;
      } else {
        return d.deleted_at === null;
      }
    });

    // Search term
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      result = result.filter(
        d =>
          d.id.toLowerCase().includes(term) ||
          d.subscriber_name.toLowerCase().includes(term) ||
          d.subscriber_phone.includes(term) ||
          d.reason.toLowerCase().includes(term)
      );
    }

    // Status filter
    if (statusFilter) {
      result = result.filter(d => d.status === statusFilter);
    }

    // Customer filter
    if (customerFilter) {
      result = result.filter(d => d.subscriber_id === customerFilter);
    }

    // Installment filter
    if (typeFilter !== 'ALL') {
      const isInstallment = typeFilter === 'INSTALLMENT';
      result = result.filter(d => d.is_installment === isInstallment);
    }

    // Sorting
    result.sort((a, b) => {
      let fieldA = a[sortField];
      let fieldB = b[sortField];

      if (fieldA === null || fieldA === undefined) return 1;
      if (fieldB === null || fieldB === undefined) return -1;

      if (typeof fieldA === 'string') {
        fieldA = fieldA.toLowerCase();
        fieldB = (fieldB as string).toLowerCase();
      }

      if (fieldA < fieldB) return sortDirection === 'asc' ? -1 : 1;
      if (fieldA > fieldB) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });

    return result;
  }, [debts, searchTerm, statusFilter, customerFilter, typeFilter, showDeleted, sortField, sortDirection]);

  // Pagination slicing
  const paginatedDebts = useMemo(() => {
    const start = (page - 1) * pageSize;
    return processedDebts.slice(start, start + pageSize);
  }, [processedDebts, page, pageSize]);

  const totalPages = Math.ceil(processedDebts.length / pageSize) || 1;

  // Reset page when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, statusFilter, customerFilter, typeFilter, showDeleted]);

  // Toggle sorting
  const handleSort = (field: keyof Debt) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Add Debt action
  const handleAddDebt = async (e: React.FormEvent) => {
    e.preventDefault();
    
    let subId = '';
    let subName = '';
    let subPhone = '';

    if (subscriberType === 'manual') {
      if (!formData.manual_name || !formData.amount || !formData.due_date) {
        alert('يرجى ملء جميع الحقول المطلوبة.');
        return;
      }
      subId = `MANUAL-${Date.now()}`;
      subName = formData.manual_name;
      subPhone = formData.manual_phone || '—';
    } else {
      if (!formData.subscriber_id || !formData.amount || !formData.due_date) {
        alert('يرجى ملء جميع الحقول المطلوبة.');
        return;
      }
      let customer = allCustomers.find(c => String(c.id) === String(formData.subscriber_id));
      if (!customer && preSelectedCustomerId && String(formData.subscriber_id) === String(preSelectedCustomerId)) {
        customer = {
          id: String(preSelectedCustomerId),
          name: preSelectedCustomerName || '—',
          phone: preSelectedCustomerPhone || '—'
        };
      }
      if (!customer) {
        alert('المشترك المحدد غير موجود.');
        return;
      }
      subId = customer.id;
      subName = customer.name;
      subPhone = customer.phone;
    }

    await debtsService.createDebt({
      subscriber_id: subId,
      subscriber_name: subName,
      subscriber_phone: subPhone,
      amount: Number(formData.amount),
      reason: formData.reason || '—',
      due_date: formData.due_date,
      notes: formData.notes,
      is_installment: formData.is_installment,
      attachments: formData.attachments,
    });

    setIsAddModalOpen(false);
    // Reset form
    setFormData({
      subscriber_id: '',
      manual_name: '',
      manual_phone: '',
      amount: '',
      reason: '',
      due_date: '',
      notes: '',
      is_installment: false,
      attachments: [],
    });
    setCustomerSearchQuery('');
    setFileUploadSim(null);
    await refreshData();
  };

  // Edit Debt action
  const handleEditDebt = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDebt) return;

    await debtsService.updateDebt(selectedDebt.id, {
      amount: Number(formData.amount),
      due_date: formData.due_date,
      notes: formData.notes,
      reason: formData.reason,
    });

    setIsEditModalOpen(false);
    setSelectedDebt(null);
    await refreshData();
  };

  // Soft Delete action
  const handleDeleteDebt = (id: string) => {
    setDeleteConfirm({
      isOpen: true,
      type: 'DEBT',
      id,
      message: 'هل أنت متأكد من حذف هذا الدين مؤقتًا؟ يمكنك استعادته لاحقاً.',
    });
  };

  // Restore action
  const handleRestoreDebt = async (id: string) => {
    await debtsService.restoreDebt(id);
    await refreshData();
  };

  // Add Payment action
  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedDebt || !paymentFormData.amount || !paymentFormData.receipt_number) {
      alert('يرجى ملء جميع حقول الدفعة.');
      return;
    }

    await debtsService.createPayment({
      debt_id: selectedDebt.id,
      amount: Number(paymentFormData.amount),
      payment_date: new Date().toISOString(),
      payment_method: paymentFormData.payment_method,
      receipt_number: paymentFormData.receipt_number,
      notes: paymentFormData.notes,
    });

    setIsPaymentModalOpen(false);
    // Refresh selected debt to update modal view
    const updated = await debtsService.getDebt(selectedDebt.id);
    if (updated) setSelectedDebt(updated);
    
    // Reset payment form
    setPaymentFormData({
      amount: '',
      payment_method: 'CASH',
      receipt_number: '',
      notes: '',
    });
    await refreshData();
  };

  // Delete Payment action
  const handleDeletePayment = (payId: string, debtId: string) => {
    setDeleteConfirm({
      isOpen: true,
      type: 'PAYMENT',
      id: payId,
      extraId: debtId,
      message: 'هل أنت متأكد من حذف هذه الدفعة؟ سيتم إعادة احتساب المبلغ المتبقي.',
    });
  };

  // Simulated File Upload
  const handleSimulateUpload = () => {
    if (!fileUploadSim) return;
    const newAttach: DebtAttachment = {
      name: fileUploadSim,
      size: Math.floor(Math.random() * 5 * 1024 * 1024) + 102400, // random size up to 5MB
      type: fileUploadSim.endsWith('.pdf') ? 'application/pdf' : 'image/jpeg',
      url: '#',
      uploadedAt: new Date().toISOString()
    };
    setFormData(prev => ({
      ...prev,
      attachments: [...prev.attachments, newAttach]
    }));
    setFileUploadSim(null);
  };

  // CSV Export
  const handleExportCSV = () => {
    const headers = [
      'رقم الدين',
      'اسم المشترك',
      'الهاتف',
      'المبلغ الأصلي',
      'المسدد',
      'المتبقي',
      'السبب',
      'تاريخ الاستحقاق',
      'الحالة',
      'مقسط'
    ];

    const rows = processedDebts.map(d => [
      d.id,
      d.subscriber_name,
      d.subscriber_phone,
      d.amount,
      d.paid_amount,
      d.remaining_amount,
      d.reason,
      d.due_date,
      statusLabels[d.status],
      d.is_installment ? 'نعم' : 'لا'
    ]);

    const csvContent =
      '\uFEFF' + // UTF-8 BOM for Excel
      [headers.join(','), ...rows.map(e => e.map(val => `"${String(val).replace(/"/g, '""')}"`).join(','))].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `debts_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div style={{ padding: '24px', direction: 'rtl', minHeight: '100%', boxSizing: 'border-box' }}>
      {/* Top Header */}
      <div
        style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          justifyContent: 'space-between',
          alignItems: isMobile ? 'stretch' : 'center',
          marginBottom: '24px',
          gap: isMobile ? '16px' : '10px'
        }}
      >
        <div>
          <h1 style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '24px', fontWeight: 800, margin: 0 }}>
            إدارة ديون المشتركين
          </h1>
          <p style={{ color: isLight ? '#64748b' : '#94a3b8', fontSize: '13px', marginTop: '4px' }}>
            نظام متكامل لمتابعة ديون المقاولين، وجدولة الدفعات وسجلات العمليات المالية.
          </p>
        </div>
        <div style={{ display: 'flex', gap: '10px', width: isMobile ? '100%' : 'auto' }}>
          <button
            onClick={() => {
              setFormData({
                subscriber_id: '',
                manual_name: '',
                manual_phone: '',
                amount: '',
                reason: '',
                due_date: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                notes: '',
                is_installment: false,
                attachments: [],
              });
              setIsAddModalOpen(true);
            }}
            style={{
              background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
              color: '#ffffff',
              border: 'none',
              borderRadius: '8px',
              padding: isMobile ? '12px 16px' : '10px 18px',
              fontSize: '14px',
              fontWeight: 700,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              cursor: 'pointer',
              boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
              width: isMobile ? '100%' : 'auto',
              boxSizing: 'border-box'
            }}
          >
            <Plus size={16} />
            <span>إضافة دين جديد</span>
          </button>
        </div>
      </div>

      {/* Tabs Navigation */}
      <div
        style={isMobile ? {
          display: 'grid',
          gridTemplateColumns: 'repeat(2, 1fr)',
          gap: '10px',
          marginBottom: '20px',
          width: '100%',
          boxSizing: 'border-box'
        } : {
          display: 'flex',
          borderBottom: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
          marginBottom: '24px',
          gap: '16px'
        }}
      >
        {[
          { id: 'dashboard', label: 'لوحة الإحصائيات', icon: LayoutDashboard, color: '#3b82f6', rgb: '59, 130, 246' },
          { id: 'list', label: 'قائمة الديون', icon: Coins, color: '#10b981', rgb: '16, 185, 129' },
          { id: 'alerts', label: 'التنبيهات الذكية', icon: Bell, badge: smartAlerts.overdue.length + smartAlerts.dueToday.length, color: '#ef4444', rgb: '239, 68, 68' },
          { id: 'logs', label: 'سجل العمليات', icon: History, color: '#8b5cf6', rgb: '139, 92, 246' }
        ].map(tab => {
          const isActive = activeTab === tab.id;
          const Icon = tab.icon;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={isMobile ? {
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                padding: '10px 12px',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                position: 'relative',
                boxSizing: 'border-box',
                border: isActive ? `2px solid ${tab.color}` : `1.5px solid ${isLight ? 'rgba(0,0,0,0.06)' : 'rgba(255,255,255,0.08)'}`,
                background: isActive 
                  ? (isLight ? `rgba(${tab.rgb}, 0.08)` : `rgba(${tab.rgb}, 0.15)`) 
                  : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)'),
                color: isActive ? tab.color : (isLight ? '#475569' : '#94a3b8'),
                boxShadow: isActive ? `0 4px 12px rgba(${tab.rgb}, 0.15)` : 'none',
                fontWeight: isActive ? 700 : 500,
                fontSize: '12px',
                height: '46px'
              } : {
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '12px 6px',
                border: 'none',
                background: 'transparent',
                borderBottom: isActive ? '3px solid #3b82f6' : '3px solid transparent',
                color: isActive ? '#3b82f6' : (isLight ? '#64748b' : '#94a3b8'),
                fontSize: '14px',
                fontWeight: isActive ? 700 : 500,
                cursor: 'pointer',
                transition: 'all 0.2s',
                position: 'relative'
              }}
            >
              <Icon size={16} style={{ color: isActive ? tab.color : (isLight ? '#64748b' : '#94a3b8'), transition: 'color 0.2s' }} />
              <span style={{ fontSize: isMobile ? '12px' : '14px' }}>{tab.label}</span>
              {tab.badge ? (
                <span
                  style={isMobile ? {
                    background: '#ef4444',
                    color: '#ffffff',
                    fontSize: '10px',
                    fontWeight: 700,
                    borderRadius: '10px',
                    padding: '1px 5px',
                    marginRight: '4px'
                  } : {
                    background: '#ef4444',
                    color: '#ffffff',
                    fontSize: '10px',
                    fontWeight: 700,
                    borderRadius: '10px',
                    padding: '1px 6px',
                    marginRight: '4px'
                  }}
                >
                  {tab.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Tab Contents */}
      {activeTab === 'dashboard' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Stats Grid */}
          <div className="debts-stats-grid" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '16px' }}>
            <StatsCard
              title="إجمالي الديون القائمة"
              value={`${stats.totalDebts.toLocaleString()} د.ع`}
              color="#3b82f6"
              isLight={isLight}
              icon={<Coins size={22} />}
            />
            <StatsCard
              title="المبالغ المسددة"
              value={`${stats.totalPaid.toLocaleString()} د.ع`}
              color="#10b981"
              isLight={isLight}
              icon={<CheckCircle size={22} />}
            />
            <StatsCard
              title="المبالغ المتبقية"
              value={`${stats.totalRemaining.toLocaleString()} د.ع`}
              color="#f59e0b"
              isLight={isLight}
              icon={<DollarSign size={22} />}
            />
            <StatsCard
              title="الديون النشطة"
              value={stats.activeCount}
              color="#6366f1"
              isLight={isLight}
              icon={<Clock size={22} />}
            />
            <StatsCard
              title="الديون المتأخرة"
              value={stats.overdueCount}
              color="#ef4444"
              isLight={isLight}
              icon={<AlertCircle size={22} />}
            />
          </div>

          {/* Charts Row */}
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 0.8fr', gap: '20px' }}>
            {/* Top Debtors Progress Bars */}
            <div
              style={{
                background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
                border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: '12px',
                padding: '20px'
              }}
            >
              <h3 style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0' }}>
                أعلى 10 مدينين متبقيين
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px' }}>
                {stats.topDebtors.length === 0 ? (
                  <div style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '24px' }}>لا توجد ديون مسجلة حالياً</div>
                ) : (
                  stats.topDebtors.map((td, i) => {
                    const percent = td.total > 0 ? (td.paid / td.total) * 100 : 0;
                    return (
                      <div key={i}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '13px', marginBottom: '6px' }}>
                          <span style={{ color: isLight ? '#1e293b' : '#cbd5e1', fontWeight: 600 }}>{td.name}</span>
                          <span style={{ color: isLight ? '#475569' : '#94a3b8' }}>
                            {td.remaining.toLocaleString()} د.ع متبقي (تم سداد {percent.toFixed(0)}%)
                          </span>
                        </div>
                        <div style={{ width: '100%', height: '8px', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)', borderRadius: '999px', overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${percent}%`,
                              height: '100%',
                              background: '#10b981',
                              borderRadius: '999px',
                              transition: 'width 0.4s'
                            }}
                          />
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
            </div>

            {/* Overdue list */}
            <div
              style={{
                background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
                border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
                borderRadius: '12px',
                padding: '20px'
              }}
            >
              <h3 style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '16px', fontWeight: 700, margin: '0 0 16px 0' }}>
                أكثر المشتركين تأخيراً في السداد
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxHeight: '280px', overflowY: 'auto' }}>
                {stats.overdueDebtors.length === 0 ? (
                  <div style={{ color: '#64748b', fontSize: '13px', textAlign: 'center', padding: '24px' }}>لا توجد متأخرات سداد حالياً</div>
                ) : (
                  stats.overdueDebtors.map((od, i) => (
                    <div
                      key={i}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '10px 12px',
                        background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.03)',
                        borderRadius: '8px',
                        borderRight: '4px solid #ef4444'
                      }}
                    >
                      <div>
                        <div style={{ color: isLight ? '#1e293b' : '#f1f5f9', fontWeight: 600, fontSize: '13px' }}>{od.name}</div>
                        <div style={{ color: '#ef4444', fontSize: '11px', marginTop: '2px', fontWeight: 600 }}>
                          متأخر منذ {od.daysOverdue} يوم
                        </div>
                      </div>
                      <div style={{ color: isLight ? '#0f172a' : '#cbd5e1', fontWeight: 700, fontSize: '13px' }}>
                        {od.amount.toLocaleString()} د.ع
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'list' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          {/* Filters Row */}
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '12px',
              alignItems: 'center',
              justifyContent: 'space-between',
              background: isLight ? '#f8fafc' : 'rgba(13,31,60,0.4)',
              border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'}`,
              borderRadius: '10px',
              padding: '12px 16px'
            }}
          >
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', flex: 1 }}>
              {/* Search */}
              <div style={{ position: 'relative', minWidth: '200px' }}>
                <Search size={16} color="#64748b" style={{ position: 'absolute', right: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                <input
                  type="text"
                  placeholder="بحث برقم الدين، المشترك..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  style={{
                    width: '100%',
                    padding: '8px 34px 8px 12px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    borderRadius: '6px',
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
              </div>

              {/* Status Filter */}
              <select
                value={statusFilter}
                onChange={e => setStatusFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                  borderRadius: '6px',
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  fontSize: '13px',
                  outline: 'none',
                  minWidth: '120px'
                }}
              >
                <option value="">كل الحالات</option>
                <option value="ACTIVE">نشط</option>
                <option value="PAID">مسدد بالكامل</option>
                <option value="OVERDUE">متأخر</option>
              </select>

              {/* Customer Filter */}
              <select
                value={customerFilter}
                onChange={e => setCustomerFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                  borderRadius: '6px',
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  fontSize: '13px',
                  outline: 'none',
                  minWidth: '150px'
                }}
              >
                <option value="">كل المشتركين</option>
                {allCustomers.map(c => (
                  <option key={c.id} value={c.id}>{c.name}</option>
                ))}
              </select>

              {/* Type Filter */}
              <select
                value={typeFilter}
                onChange={e => setTypeFilter(e.target.value)}
                style={{
                  padding: '8px 12px',
                  background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                  borderRadius: '6px',
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  fontSize: '13px',
                  outline: 'none',
                  minWidth: '120px'
                }}
              >
                <option value="ALL">كل الديون</option>
                <option value="INSTALLMENT">مقسطة</option>
                <option value="ONETIME">دفعة واحدة</option>
              </select>

              {/* Toggle Deleted */}
              <button
                onClick={() => setShowDeleted(prev => !prev)}
                style={{
                  padding: '8px 14px',
                  background: showDeleted ? 'rgba(239, 68, 68, 0.1)' : 'rgba(255,255,255,0.06)',
                  border: `1px solid ${showDeleted ? 'rgba(239, 68, 68, 0.25)' : (isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)')}`,
                  borderRadius: '6px',
                  color: showDeleted ? '#f87171' : (isLight ? '#475569' : '#94a3b8'),
                  fontSize: '13px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  transition: 'all 0.2s'
                }}
              >
                <Trash2 size={14} />
                <span>المحذوف مؤقتاً</span>
              </button>
            </div>

            {/* Export Buttons */}
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={handleExportCSV}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  background: 'rgba(16, 185, 129, 0.1)',
                  border: '1px solid rgba(16, 185, 129, 0.25)',
                  borderRadius: '6px',
                  padding: '8px 14px',
                  color: '#34d399',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: 600
                }}
              >
                <Download size={14} />
                <span>تصدير CSV</span>
              </button>
            </div>
          </div>

          {/* Table Container */}
          <div
            style={{
              background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
              border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: '12px',
              overflowX: 'auto',
              boxShadow: isLight ? '0 4px 6px rgba(0,0,0,0.02)' : 'none'
            }}
          >
            <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '13.5px' }}>
              <thead>
                <tr style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)', borderBottom: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}` }}>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600 }}># رقم الدين</th>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleSort('subscriber_name')}>
                    المشترك <ArrowUpDown size={12} style={{ display: 'inline-block', marginRight: '4px' }} />
                  </th>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600 }}>مبلغ الدين</th>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600 }}>المدفوع</th>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600 }}>المتبقي</th>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600 }}>السبب</th>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600, cursor: 'pointer' }} onClick={() => handleSort('due_date')}>
                    تاريخ الاستحقاق <ArrowUpDown size={12} style={{ display: 'inline-block', marginRight: '4px' }} />
                  </th>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600 }}>الحالة</th>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600 }}>الدفعات</th>
                  <th style={{ padding: '14px 16px', color: '#64748b', fontWeight: 600, textAlign: 'left' }}>الإجراءات</th>
                </tr>
              </thead>
              <tbody>
                {paginatedDebts.length === 0 ? (
                  <tr>
                    <td colSpan={10} style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                      لا توجد سجلات ديون تطابق الفلاتر المحددة.
                    </td>
                  </tr>
                ) : (
                  paginatedDebts.map((d, i) => (
                    <tr
                      key={d.id}
                      style={{
                        borderBottom: `1px solid ${isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)'}`,
                        background: i % 2 === 0 ? 'transparent' : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.01)'),
                        transition: 'background 0.15s ease'
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.03)';
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.background = i % 2 === 0 ? 'transparent' : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.01)');
                      }}
                    >
                      <td data-label="# رقم الدين" style={{ padding: '12px 16px', color: '#3b82f6', fontWeight: 700 }}>{d.id}</td>
                      <td data-label="المشترك" style={{ padding: '12px 16px' }}>
                        <div>
                          <div style={{ color: isLight ? '#1e293b' : '#f1f5f9', fontWeight: 600 }}>{d.subscriber_name}</div>
                          <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>{d.subscriber_phone}</div>
                        </div>
                      </td>
                      <td data-label="مبلغ الدين" style={{ padding: '12px 16px', color: isLight ? '#1e293b' : '#cbd5e1', fontWeight: 700 }}>
                        {d.amount.toLocaleString()} د.ع
                      </td>
                      <td data-label="المدفوع" style={{ padding: '12px 16px', color: '#10b981', fontWeight: 600 }}>
                        {d.paid_amount.toLocaleString()} د.ع
                      </td>
                      <td data-label="المتبقي" style={{ padding: '12px 16px', color: d.remaining_amount > 0 ? '#f59e0b' : '#64748b', fontWeight: 700 }}>
                        {d.remaining_amount.toLocaleString()} د.ع
                      </td>
                      <td data-label="السبب" style={{ padding: '12px 16px', color: isLight ? '#475569' : '#94a3b8' }}>{d.reason}</td>
                      <td style={{ padding: '12px 16px', color: isLight ? '#1e293b' : '#cbd5e1' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                          <Calendar size={13} color="#64748b" />
                          <span>{d.due_date}</span>
                        </div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            background: d.status === 'PAID' ? 'rgba(16, 185, 129, 0.12)' : (d.status === 'OVERDUE' ? 'rgba(239, 68, 68, 0.12)' : 'rgba(59, 130, 246, 0.12)'),
                            color: d.status === 'PAID' ? '#10b981' : (d.status === 'OVERDUE' ? '#ef4444' : '#3b82f6'),
                            border: `1px solid ${d.status === 'PAID' ? 'rgba(16, 185, 129, 0.25)' : (d.status === 'OVERDUE' ? 'rgba(239, 68, 68, 0.25)' : 'rgba(59, 130, 246, 0.25)')}`,
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 700
                          }}
                        >
                          {statusLabels[d.status]}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ color: isLight ? '#1e293b' : '#cbd5e1', fontWeight: 600 }}>{d.installments_count} دفعات</span>
                        {d.is_installment && (
                          <span style={{ fontSize: '10px', color: '#3b82f6', background: 'rgba(59,130,246,0.08)', padding: '1px 4px', borderRadius: '3px', marginRight: '6px' }}>مقسط</span>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px', textAlign: 'left' }}>
                        <div style={{ display: 'flex', gap: '6px', justifyContent: 'flex-start' }}>
                          {d.deleted_at === null ? (
                            <>
                              <button
                                onClick={() => {
                                  setSelectedDebt(d);
                                  setIsDetailsModalOpen(true);
                                }}
                                title="عرض التفاصيل والدفعات"
                                style={{
                                  background: 'rgba(255, 255, 255, 0.05)',
                                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`,
                                  borderRadius: '6px',
                                  padding: '6px',
                                  cursor: 'pointer',
                                  color: isLight ? '#475569' : '#e2e8f0'
                                }}
                              >
                                <Eye size={14} />
                              </button>
                              {d.status !== 'PAID' && (
                                <button
                                  onClick={() => {
                                    setSelectedDebt(d);
                                    setPaymentFormData({
                                      amount: String(d.remaining_amount),
                                      receipt_number: `REC-${Math.floor(Math.random() * 90000) + 10000}`,
                                      payment_method: 'CASH',
                                      notes: '',
                                    });
                                    setIsPaymentModalOpen(true);
                                  }}
                                  title="تسديد الدين"
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    background: 'rgba(16, 185, 129, 0.1)',
                                    border: '1px solid rgba(16, 185, 129, 0.25)',
                                    borderRadius: '6px',
                                    padding: '5px 10px',
                                    cursor: 'pointer',
                                    color: '#10b981',
                                    fontSize: '12px',
                                    fontWeight: 600
                                  }}
                                >
                                  <DollarSign size={13} />
                                  <span>تسديد</span>
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  setSelectedDebt(d);
                                  setFormData({
                                    subscriber_id: d.subscriber_id,
                                    manual_name: '',
                                    manual_phone: '',
                                    amount: String(d.amount),
                                    reason: d.reason,
                                    due_date: d.due_date,
                                    notes: d.notes,
                                    is_installment: d.is_installment,
                                    attachments: d.attachments,
                                  });
                                  setIsEditModalOpen(true);
                                }}
                                title="تعديل الدين"
                                style={{
                                  background: 'rgba(255, 255, 255, 0.05)',
                                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`,
                                  borderRadius: '6px',
                                  padding: '6px',
                                  cursor: 'pointer',
                                  color: '#3b82f6'
                                }}
                              >
                                <Edit size={14} />
                              </button>
                              <button
                                onClick={() => handleDeleteDebt(d.id)}
                                title="حذف"
                                style={{
                                  background: 'rgba(239, 68, 68, 0.05)',
                                  border: '1px solid rgba(239, 68, 68, 0.2)',
                                  borderRadius: '6px',
                                  padding: '6px',
                                  cursor: 'pointer',
                                  color: '#ef4444'
                                }}
                              >
                                <Trash2 size={14} />
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => handleRestoreDebt(d.id)}
                              title="استعادة الدين المحذوف"
                              style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                background: 'rgba(16, 185, 129, 0.08)',
                                border: '1px solid rgba(16, 185, 129, 0.2)',
                                borderRadius: '6px',
                                padding: '5px 10px',
                                cursor: 'pointer',
                                color: '#10b981',
                                fontSize: '11px',
                                fontWeight: 700
                              }}
                            >
                              <RotateCcw size={12} />
                              <span>استعادة</span>
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            {/* Pagination controls */}
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                padding: '12px 16px',
                borderTop: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
                background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.01)'
              }}
            >
              <span style={{ color: '#64748b', fontSize: '12px' }}>
                يعرض {paginatedDebts.length} من أصل {processedDebts.length} ديون مسجلة
              </span>

              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '6px',
                    padding: '4px 10px',
                    color: isLight ? '#0f172a' : '#cbd5e1',
                    cursor: page === 1 ? 'not-allowed' : 'pointer',
                    opacity: page === 1 ? 0.4 : 1
                  }}
                >
                  السابق
                </button>
                <span style={{ color: isLight ? '#1e293b' : '#cbd5e1', fontSize: '13px', fontWeight: 600 }}>
                  صفحة {page} من {totalPages}
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  style={{
                    background: 'rgba(255,255,255,0.04)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
                    borderRadius: '6px',
                    padding: '4px 10px',
                    color: isLight ? '#0f172a' : '#cbd5e1',
                    cursor: page === totalPages ? 'not-allowed' : 'pointer',
                    opacity: page === totalPages ? 0.4 : 1
                  }}
                >
                  التالي
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'alerts' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
            {/* Overdue Alerts Card */}
            <AlertSectionCard
              title="ديون متأخرة السداد"
              debts={smartAlerts.overdue}
              color="#ef4444"
              isLight={isLight}
              onView={(d) => {
                setSelectedDebt(d);
                setIsDetailsModalOpen(true);
              }}
            />
            {/* Due Today Card */}
            <AlertSectionCard
              title="ديون تستحق اليوم"
              debts={smartAlerts.dueToday}
              color="#f59e0b"
              isLight={isLight}
              onView={(d) => {
                setSelectedDebt(d);
                setIsDetailsModalOpen(true);
              }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '20px' }}>
            {/* Due 3 Days Card */}
            <AlertSectionCard
              title="تستحق خلال 3 أيام"
              debts={smartAlerts.dueIn3Days}
              color="#3b82f6"
              isLight={isLight}
              onView={(d) => {
                setSelectedDebt(d);
                setIsDetailsModalOpen(true);
              }}
            />
            {/* Due 7 Days Card */}
            <AlertSectionCard
              title="تستحق خلال 7 أيام"
              debts={smartAlerts.dueIn7Days}
              color="#6366f1"
              isLight={isLight}
              onView={(d) => {
                setSelectedDebt(d);
                setIsDetailsModalOpen(true);
              }}
            />
          </div>
        </div>
      )}

      {activeTab === 'logs' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
          <div
            style={{
              background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
              border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
              borderRadius: '12px',
              overflowX: 'auto',
            }}
          >
            <table className="responsive-table" style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'right', fontSize: '13px' }}>
              <thead>
                <tr style={{ background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.02)', borderBottom: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}` }}>
                  <th style={{ padding: '12px 16px', color: '#64748b', fontWeight: 600 }}>الوقت والتاريخ</th>
                  <th style={{ padding: '12px 16px', color: '#64748b', fontWeight: 600 }}>المستخدم</th>
                  <th style={{ padding: '12px 16px', color: '#64748b', fontWeight: 600 }}>العملية</th>
                  <th style={{ padding: '12px 16px', color: '#64748b', fontWeight: 600 }}>البيان</th>
                  <th style={{ padding: '12px 16px', color: '#64748b', fontWeight: 600 }}>عنوان IP</th>
                </tr>
              </thead>
              <tbody>
                {logs.length === 0 ? (
                  <tr>
                    <td colSpan={5} style={{ padding: '36px', textAlign: 'center', color: '#64748b' }}>
                      لا توجد عمليات مسجلة في النظام.
                    </td>
                  </tr>
                ) : (
                  logs.map((log, i) => (
                    <tr
                      key={log.id}
                      style={{
                        borderBottom: `1px solid ${isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)'}`,
                        background: i % 2 === 0 ? 'transparent' : (isLight ? '#f8fafc' : 'rgba(255,255,255,0.01)')
                      }}
                    >
                      <td data-label="الوقت والتاريخ" style={{ padding: '12px 16px', color: '#64748b' }}>
                        {new Date(log.created_at).toLocaleString('ar-IQ')}
                      </td>
                      <td data-label="المستخدم" style={{ padding: '12px 16px', fontWeight: 600, color: isLight ? '#1e293b' : '#e2e8f0' }}>
                        {log.user_id}
                      </td>
                      <td data-label="العملية" style={{ padding: '12px 16px' }}>
                        <span
                          style={{
                            background:
                              log.action === 'CREATE'
                                ? 'rgba(59, 130, 246, 0.12)'
                                : log.action === 'ADD_PAYMENT'
                                ? 'rgba(16, 185, 129, 0.12)'
                                : log.action === 'DELETE'
                                ? 'rgba(239, 68, 68, 0.12)'
                                : 'rgba(107, 114, 128, 0.12)',
                            color:
                              log.action === 'CREATE'
                                ? '#3b82f6'
                                : log.action === 'ADD_PAYMENT'
                                ? '#10b981'
                                : log.action === 'DELETE'
                                ? '#ef4444'
                                : '#6b7280',
                            padding: '2px 8px',
                            borderRadius: '4px',
                            fontSize: '11px',
                            fontWeight: 700
                          }}
                        >
                          {log.action}
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px', color: isLight ? '#1e293b' : '#cbd5e1' }}>
                        {log.description}
                      </td>
                      <td style={{ padding: '12px 16px', color: '#64748b', fontFamily: 'monospace' }}>
                        {log.ip_address}
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* ==================== ADD DEBT MODAL ==================== */}
      {isAddModalOpen && (
        <ModalWrapper title="إضافة دين جديد" isLight={isLight} onClose={() => setIsAddModalOpen(false)}>
          <form onSubmit={handleAddDebt} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            {/* Subscriber Section - Full Width Row */}
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '8px' }}>المشترك الملتزم *</label>
              
              {/* Segmented Control Tabs */}
              <div style={{
                display: 'flex',
                background: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.05)',
                padding: '4px',
                borderRadius: '8px',
                marginBottom: '12px',
                gap: '4px',
                border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.08)'}`
              }}>
                <button
                  type="button"
                  onClick={() => setSubscriberType('system')}
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '6px',
                    border: 'none',
                    background: subscriberType === 'system' ? (isLight ? '#ffffff' : 'rgba(59, 130, 246, 0.25)') : 'transparent',
                    color: subscriberType === 'system' ? (isLight ? '#0f172a' : '#60a5fa') : '#64748b',
                    fontSize: '13px',
                    fontWeight: subscriberType === 'system' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  البحث واختيار مشترك من النظام
                </button>
                <button
                  type="button"
                  onClick={() => setSubscriberType('manual')}
                  style={{
                    flex: 1,
                    padding: '8px',
                    borderRadius: '6px',
                    border: 'none',
                    background: subscriberType === 'manual' ? (isLight ? '#ffffff' : 'rgba(59, 130, 246, 0.25)') : 'transparent',
                    color: subscriberType === 'manual' ? (isLight ? '#0f172a' : '#60a5fa') : '#64748b',
                    fontSize: '13px',
                    fontWeight: subscriberType === 'manual' ? 'bold' : 'normal',
                    cursor: 'pointer',
                    transition: 'all 0.2s'
                  }}
                >
                  إدخال مشترك جديد يدوياً
                </button>
              </div>

              {subscriberType === 'system' ? (
                /* Searchable Input Dropdown */
                <div style={{ position: 'relative' }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ position: 'relative' }}>
                    <input
                      type="text"
                      placeholder="ابحث عن مشترك بالاسم، الرقم، أو الكود..."
                      value={customerSearchQuery}
                      onChange={(e) => {
                        const val = e.target.value;
                        setCustomerSearchQuery(val);
                        setIsCustomerDropdownOpen(true);
                        
                        // Try to find an exact match by name or ID or formatted name
                        const exactMatch = allCustomers.find(c => 
                          val === `${c.name} (${c.id})` ||
                          val.trim() === c.name ||
                          val.trim() === String(c.id)
                        );
                        
                        if (exactMatch) {
                          setFormData(p => ({ ...p, subscriber_id: exactMatch.id }));
                        } else {
                          // If it's the pre-selected customer and the value matches the pre-selected customer formatted name
                          if (preSelectedCustomerId && val === `${preSelectedCustomerName} (${preSelectedCustomerId})`) {
                            setFormData(p => ({ ...p, subscriber_id: preSelectedCustomerId }));
                          } else {
                            setFormData(p => ({ ...p, subscriber_id: '' }));
                          }
                        }
                      }}
                      onFocus={() => setIsCustomerDropdownOpen(true)}
                      style={{
                        width: '100%',
                        padding: '10px 12px 10px 32px',
                        borderRadius: '6px',
                        background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                        border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                        color: isLight ? '#0f172a' : '#e2e8f0',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                    {formData.subscriber_id && (
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(p => ({ ...p, subscriber_id: '' }));
                          setCustomerSearchQuery('');
                        }}
                        style={{
                          position: 'absolute',
                          left: '10px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          color: '#ef4444',
                          cursor: 'pointer',
                          fontSize: '16px',
                          padding: '4px'
                        }}
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {isCustomerDropdownOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        marginTop: '4px',
                        maxHeight: '200px',
                        overflowY: 'auto',
                        background: isLight ? '#ffffff' : '#081120',
                        border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                        borderRadius: '6px',
                        boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.3)',
                        zIndex: 50
                      }}
                    >
                      {(() => {
                        const filtered = allCustomers.filter(c =>
                          c.name.toLowerCase().includes(customerSearchQuery.toLowerCase()) ||
                          c.id.toLowerCase().includes(customerSearchQuery.toLowerCase())
                        );

                        if (filtered.length === 0) {
                          return (
                            <div style={{ padding: '10px', color: '#64748b', textAlign: 'center', fontSize: '13px' }}>
                              لا توجد نتائج مطابقة
                            </div>
                          );
                        }

                        return filtered.map(c => (
                          <div
                            key={c.id}
                            onClick={() => {
                              setFormData(p => ({ ...p, subscriber_id: c.id }));
                              setCustomerSearchQuery(`${c.name} (${c.id})`);
                              setIsCustomerDropdownOpen(false);
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              background: formData.subscriber_id === c.id ? 'rgba(59, 130, 246, 0.15)' : 'transparent',
                              borderBottom: `1px solid ${isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.05)'}`,
                              color: isLight ? '#0f172a' : '#e2e8f0',
                              fontSize: '13px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              transition: 'background-color 0.2s'
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.05)'}
                            onMouseLeave={(e) => e.currentTarget.style.background = formData.subscriber_id === c.id ? 'rgba(59, 130, 246, 0.15)' : 'transparent'}
                          >
                            <span>{c.name}</span>
                            <span style={{ color: '#64748b', fontSize: '11px', fontFamily: 'monospace' }}>{c.id}</span>
                          </div>
                        ));
                      })()}
                    </div>
                  )}
                </div>
              ) : (
                /* Manual subscriber entry fields */
                <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '12px' }}>
                  <div>
                    <input
                      type="text"
                      placeholder="اسم المشترك الجديد *"
                      required={subscriberType === 'manual'}
                      value={formData.manual_name}
                      onChange={(e) => setFormData(p => ({ ...p, manual_name: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                        border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                        color: isLight ? '#0f172a' : '#e2e8f0',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                  <div>
                    <input
                      type="text"
                      placeholder="رقم الهاتف (مثال: 07800000000)"
                      value={formData.manual_phone}
                      onChange={(e) => setFormData(p => ({ ...p, manual_phone: e.target.value }))}
                      style={{
                        width: '100%',
                        padding: '10px 12px',
                        borderRadius: '6px',
                        background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                        border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                        color: isLight ? '#0f172a' : '#e2e8f0',
                        outline: 'none',
                        boxSizing: 'border-box'
                      }}
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Grid Row: Amount & Due Date */}
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>مبلغ الدين (بالدينار العراقي) *</label>
                <input
                  type="number"
                  required
                  placeholder="مثال: 150000"
                  value={formData.amount}
                  onChange={e => setFormData(p => ({ ...p, amount: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>تاريخ الاستحقاق *</label>
                <input
                  type="date"
                  required
                  value={formData.due_date}
                  onChange={e => setFormData(p => ({ ...p, due_date: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    outline: 'none',
                    boxSizing: 'border-box'
                  }}
                />
              </div>
            </div>

            {/* Full Width Row: Reason */}
            <div>
              <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>السبب / البيان الرئيسي</label>
              <input
                type="text"
                placeholder="مثال: أجهزة ONT وتمديد شبكة"
                value={formData.reason}
                onChange={e => setFormData(p => ({ ...p, reason: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  outline: 'none',
                  boxSizing: 'border-box'
                }}
              />
            </div>


            <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
              <input
                type="checkbox"
                id="is_installment"
                checked={formData.is_installment}
                onChange={e => setFormData(p => ({ ...p, is_installment: e.target.checked }))}
                style={{ width: '16px', height: '16px' }}
              />
              <label htmlFor="is_installment" style={{ color: isLight ? '#1e293b' : '#cbd5e1', fontSize: '13.5px', cursor: 'pointer' }}>
                هذا الدين مقسط ويسدد على دفعات
              </label>
            </div>

            <div>
              <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>ملاحظات وتفاصيل إضافية</label>
              <textarea
                rows={3}
                placeholder="أدخل أي ملاحظات حول الدفع أو الجدولة..."
                value={formData.notes}
                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  outline: 'none',
                  resize: 'none'
                }}
              />
            </div>

            {/* Attachments Upload Mock */}
            <div>
              <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>المرفقات والصور</label>
              <div style={{ display: 'flex', gap: '8px' }}>
                <input
                  type="text"
                  placeholder="اسم الملف لتجربة الرفع (مثال: receipt_photo.jpg)"
                  value={fileUploadSim || ''}
                  onChange={e => setFileUploadSim(e.target.value)}
                  style={{
                    flex: 1,
                    padding: '8px 10px',
                    borderRadius: '6px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    fontSize: '13px',
                    outline: 'none'
                  }}
                />
                <button
                  type="button"
                  onClick={handleSimulateUpload}
                  style={{
                    background: 'rgba(255,255,255,0.06)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`,
                    color: isLight ? '#0f172a' : '#cbd5e1',
                    padding: '6px 14px',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '13px'
                  }}
                >
                  إرفاق
                </button>
              </div>

              {/* Uploaded attachments list */}
              {formData.attachments.length > 0 && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '10px' }}>
                  {formData.attachments.map((at, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        background: 'rgba(59,130,246,0.08)',
                        border: '1px solid rgba(59,130,246,0.25)',
                        padding: '4px 8px',
                        borderRadius: '6px',
                        fontSize: '11px',
                        color: '#3b82f6'
                      }}
                    >
                      <Paperclip size={12} />
                      <span>{at.name}</span>
                      <button
                        type="button"
                        onClick={() => setFormData(p => ({ ...p, attachments: p.attachments.filter((_, i) => i !== index) }))}
                        style={{ border: 'none', background: 'transparent', color: '#ef4444', cursor: 'pointer', display: 'flex', alignItems: 'center' }}
                      >
                        <X size={10} />
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px', marginTop: '12px' }}>
              <button
                type="submit"
                style={{
                  background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                حفظ
              </button>
              <button
                type="button"
                onClick={() => setIsAddModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`,
                  color: isLight ? '#475569' : '#94a3b8',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                إلغاء
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}

      {/* ==================== EDIT DEBT MODAL ==================== */}
      {isEditModalOpen && selectedDebt && (
        <ModalWrapper title={`تعديل الدين رقم ${selectedDebt.id}`} isLight={isLight} onClose={() => setIsEditModalOpen(false)}>
          <form onSubmit={handleEditDebt} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>المشترك</label>
                <input
                  type="text"
                  disabled
                  value={selectedDebt.subscriber_name}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    background: isLight ? '#f1f5f9' : 'rgba(255, 255, 255, 0.04)',
                    border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255, 255, 255, 0.08)'}`,
                    color: '#64748b',
                    outline: 'none',
                    cursor: 'not-allowed'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>مبلغ الدين الأصلي *</label>
                <input
                  type="number"
                  required
                  value={formData.amount}
                  onChange={e => setFormData(p => ({ ...p, amount: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>السبب / البيان</label>
                <input
                  type="text"
                  value={formData.reason}
                  onChange={e => setFormData(p => ({ ...p, reason: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>تاريخ الاستحقاق *</label>
                <input
                  type="date"
                  required
                  value={formData.due_date}
                  onChange={e => setFormData(p => ({ ...p, due_date: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>ملاحظات وتفاصيل</label>
              <textarea
                rows={3}
                value={formData.notes}
                onChange={e => setFormData(p => ({ ...p, notes: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  outline: 'none',
                  resize: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px', marginTop: '12px' }}>
              <button
                type="submit"
                style={{
                  background: 'linear-gradient(90deg, #3b82f6, #6366f1)',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                تحديث
              </button>
              <button
                type="button"
                onClick={() => setIsEditModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`,
                  color: isLight ? '#475569' : '#94a3b8',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                إلغاء
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}

      {/* ==================== DEBT DETAILS & PAYMENTS MODAL ==================== */}
      {isDetailsModalOpen && selectedDebt && (
        <ModalWrapper
          title={`تفاصيل الحركة والديون - ${selectedDebt.id}`}
          isLight={isLight}
          onClose={() => setIsDetailsModalOpen(false)}
          width="850px"
        >
          <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1.2fr 0.8fr', gap: '24px' }}>
            {/* Right details column */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
              {/* Financial Summaries inside Details */}
              <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: '10px' }}>
                <div style={{ background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)', padding: '12px', borderRadius: '8px', border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'}`, textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '4px' }}>المبلغ الإجمالي</div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: isLight ? '#1e293b' : '#f1f5f9' }}>{selectedDebt.amount.toLocaleString()} د.ع</div>
                </div>
                <div style={{ background: 'rgba(16,185,129,0.06)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(16,185,129,0.15)', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#10b981', marginBottom: '4px' }}>المبلغ المسدد</div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#10b981' }}>{selectedDebt.paid_amount.toLocaleString()} د.ع</div>
                </div>
                <div style={{ background: 'rgba(245,158,11,0.06)', padding: '12px', borderRadius: '8px', border: '1px solid rgba(245,158,11,0.15)', textAlign: 'center' }}>
                  <div style={{ fontSize: '11px', color: '#f59e0b', marginBottom: '4px' }}>المبلغ المتبقي</div>
                  <div style={{ fontSize: '15px', fontWeight: 800, color: '#f59e0b' }}>{selectedDebt.remaining_amount.toLocaleString()} د.ع</div>
                </div>
              </div>

              {/* Payments Tab */}
              <div
                style={{
                  background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: '10px',
                  padding: '16px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                  <h4 style={{ margin: 0, color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '14px', fontWeight: 700 }}>
                    جدول الدفعات (Installments & Payments)
                  </h4>
                  {selectedDebt.status !== 'PAID' && (
                    <button
                      onClick={() => {
                        setPaymentFormData({
                          amount: String(selectedDebt.remaining_amount),
                          payment_method: 'CASH',
                          receipt_number: `REC-${Math.floor(Math.random() * 90000) + 10000}`,
                          notes: '',
                        });
                        setIsPaymentModalOpen(true);
                      }}
                      style={{
                        background: '#10b981',
                        color: '#ffffff',
                        border: 'none',
                        borderRadius: '6px',
                        padding: '4px 10px',
                        fontSize: '11px',
                        fontWeight: 700,
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px'
                      }}
                    >
                      <Plus size={12} />
                      <span>إضافة دفعة</span>
                    </button>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '180px', overflowY: 'auto' }}>
                  {payments.filter(p => p.debt_id === selectedDebt.id).length === 0 ? (
                    <div style={{ color: '#64748b', fontSize: '12px', textAlign: 'center', padding: '16px' }}>لا توجد أي دفعات مسجلة لهذا الدين بعد.</div>
                  ) : (
                    payments
                      .filter(p => p.debt_id === selectedDebt.id)
                      .map((p) => (
                        <div
                          key={p.id}
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '10px 12px',
                            background: isLight ? '#ffffff' : 'rgba(255,255,255,0.02)',
                            borderRadius: '8px',
                            border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.06)'}`
                          }}
                        >
                          <div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                              <span style={{ color: '#10b981', fontWeight: 700, fontSize: '13px' }}>{p.amount.toLocaleString()} د.ع</span>
                              <span style={{ fontSize: '10px', color: '#64748b', background: isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)', padding: '1px 6px', borderRadius: '3px' }}>
                                {paymentMethodLabels[p.payment_method]}
                              </span>
                            </div>
                            <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                              رقم الوصل: {p.receipt_number} | {new Date(p.payment_date).toLocaleDateString('ar-IQ')}
                            </div>
                            {p.notes && <div style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '11px', marginTop: '4px', fontStyle: 'italic' }}>"{p.notes}"</div>}
                          </div>
                          <button
                            onClick={() => handleDeletePayment(p.id, selectedDebt.id)}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: '#ef4444',
                              cursor: 'pointer',
                              padding: '4px'
                            }}
                          >
                            <Trash2 size={13} />
                          </button>
                        </div>
                      ))
                  )}
                </div>
              </div>

              {/* Attachments within Details */}
              <div
                style={{
                  background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)',
                  border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'}`,
                  borderRadius: '10px',
                  padding: '16px'
                }}
              >
                <h4 style={{ margin: '0 0 10px 0', color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '14px', fontWeight: 700 }}>
                  المرفقات والصور ({selectedDebt.attachments.length})
                </h4>
                {selectedDebt.attachments.length === 0 ? (
                  <div style={{ color: '#64748b', fontSize: '12px' }}>لا توجد مرفقات مرتبطة.</div>
                ) : (
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {selectedDebt.attachments.map((at, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px',
                          background: isLight ? '#ffffff' : 'rgba(255,255,255,0.04)',
                          border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.08)'}`,
                          padding: '6px 10px',
                          borderRadius: '6px',
                          fontSize: '11.5px',
                          color: isLight ? '#1e293b' : '#cbd5e1'
                        }}
                      >
                        <Paperclip size={12} color="#3b82f6" />
                        <span>{at.name}</span>
                        <span style={{ color: '#64748b', fontSize: '10px' }}>({(at.size / 1024).toFixed(0)} KB)</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Left timeline & info column */}
            <div
              style={{
                borderRight: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
                paddingRight: '20px',
                display: 'flex',
                flexDirection: 'column',
                gap: '16px'
              }}
            >
              {/* Core info */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '14px', fontWeight: 700 }}>
                  معلومات الدين الأساسية
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', fontSize: '13px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>المشترك:</span>
                    <span style={{ fontWeight: 600, color: isLight ? '#1e293b' : '#f1f5f9' }}>{selectedDebt.subscriber_name}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>الهاتف:</span>
                    <span style={{ fontWeight: 600, color: isLight ? '#1e293b' : '#f1f5f9', direction: 'ltr' }}>{selectedDebt.subscriber_phone}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>السبب:</span>
                    <span style={{ fontWeight: 600, color: isLight ? '#1e293b' : '#f1f5f9' }}>{selectedDebt.reason}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>تاريخ الاستحقاق:</span>
                    <span style={{ fontWeight: 600, color: isLight ? '#1e293b' : '#f1f5f9' }}>{selectedDebt.due_date}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ color: '#64748b' }}>الحالة:</span>
                    <span
                      style={{
                        fontWeight: 700,
                        color: selectedDebt.status === 'PAID' ? '#10b981' : (selectedDebt.status === 'OVERDUE' ? '#ef4444' : '#3b82f6')
                      }}
                    >
                      {statusLabels[selectedDebt.status]}
                    </span>
                  </div>
                  {selectedDebt.notes && (
                    <div style={{ marginTop: '8px', borderTop: `1px dashed ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.06)'}`, paddingTop: '8px' }}>
                      <span style={{ color: '#64748b', display: 'block', marginBottom: '4px' }}>ملاحظات:</span>
                      <p style={{ margin: 0, color: isLight ? '#475569' : '#94a3b8', lineHeight: 1.4 }}>{selectedDebt.notes}</p>
                    </div>
                  )}
                </div>
              </div>

              {/* Timeline */}
              <div>
                <h4 style={{ margin: '0 0 12px 0', color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '14px', fontWeight: 700 }}>
                  المخطط الزمني للعمليات (Timeline)
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', borderRight: '2px solid rgba(59,130,246,0.3)', paddingRight: '12px', marginRight: '6px' }}>
                  {logs
                    .filter(l => l.debt_id === selectedDebt.id)
                    .map((log) => (
                      <div key={log.id} style={{ position: 'relative' }}>
                        {/* Bullet */}
                        <div
                          style={{
                            width: '8px',
                            height: '8px',
                            borderRadius: '50%',
                            background: '#3b82f6',
                            position: 'absolute',
                            right: '-17px',
                            top: '4px'
                          }}
                        />
                        <div style={{ fontSize: '11px', color: '#64748b' }}>
                          {new Date(log.created_at).toLocaleString('ar-IQ')}
                        </div>
                        <div style={{ fontSize: '12.5px', color: isLight ? '#1e293b' : '#cbd5e1', fontWeight: 600, marginTop: '2px' }}>
                          {log.description}
                        </div>
                        <div style={{ fontSize: '10.5px', color: '#64748b' }}>
                          بواسطة: {log.user_id}
                        </div>
                      </div>
                    ))}
                </div>
              </div>
            </div>
          </div>
        </ModalWrapper>
      )}

      {/* ==================== ADD PAYMENT MODAL ==================== */}
      {isPaymentModalOpen && selectedDebt && (
        <ModalWrapper
          title={`تسجيل دفعة سداد جديدة - ${selectedDebt.id}`}
          isLight={isLight}
          onClose={() => setIsPaymentModalOpen(false)}
        >
          <form onSubmit={handleAddPayment} style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: '14px' }}>
              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>مبلغ الدفعة *</label>
                <input
                  type="number"
                  required
                  placeholder="مثال: 50000"
                  value={paymentFormData.amount}
                  onChange={e => setPaymentFormData(p => ({ ...p, amount: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    outline: 'none'
                  }}
                />
              </div>

              <div>
                <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>رقم الوصل / المعاملة *</label>
                <input
                  type="text"
                  required
                  value={paymentFormData.receipt_number}
                  onChange={e => setPaymentFormData(p => ({ ...p, receipt_number: e.target.value }))}
                  style={{
                    width: '100%',
                    padding: '10px',
                    borderRadius: '6px',
                    background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                    border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                    color: isLight ? '#0f172a' : '#e2e8f0',
                    outline: 'none'
                  }}
                />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>طريقة الدفع *</label>
              <select
                value={paymentFormData.payment_method}
                onChange={e => setPaymentFormData(p => ({ ...p, payment_method: e.target.value as any }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  outline: 'none'
                }}
              >
                <option value="CASH">نقدًا</option>
                <option value="CARD">بطاقة ائتمان</option>
                <option value="TRANSFER">تحويل إلكتروني (زين كاش، الخ...)</option>
                <option value="CHEQUE">شيك مصرفي</option>
              </select>
            </div>

            <div>
              <label style={{ display: 'block', color: '#64748b', fontSize: '13px', marginBottom: '6px' }}>ملاحظات الدفعة</label>
              <textarea
                rows={2}
                value={paymentFormData.notes}
                onChange={e => setPaymentFormData(p => ({ ...p, notes: e.target.value }))}
                style={{
                  width: '100%',
                  padding: '10px',
                  borderRadius: '6px',
                  background: isLight ? '#ffffff' : 'rgba(5, 15, 30, 0.6)',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255, 255, 255, 0.12)'}`,
                  color: isLight ? '#0f172a' : '#e2e8f0',
                  outline: 'none',
                  resize: 'none'
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-start', gap: '10px', marginTop: '12px' }}>
              <button
                type="submit"
                style={{
                  background: '#10b981',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer'
                }}
              >
                حفظ الدفعة
              </button>
              <button
                type="button"
                onClick={() => setIsPaymentModalOpen(false)}
                style={{
                  background: 'transparent',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.12)'}`,
                  color: isLight ? '#475569' : '#94a3b8',
                  borderRadius: '6px',
                  padding: '10px 24px',
                  fontSize: '14px',
                  cursor: 'pointer'
                }}
              >
                إلغاء
              </button>
            </div>
          </form>
        </ModalWrapper>
      )}

      {/* ==================== DELETE CONFIRMATION CARD MODAL ==================== */}
      {deleteConfirm.isOpen && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0,0,0,0.6)',
            backdropFilter: 'blur(4px)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 1100,
            padding: '20px',
            boxSizing: 'border-box'
          }}
        >
          <div
            style={{
              width: '100%',
              maxWidth: '400px',
              background: isLight ? '#ffffff' : '#0a1628',
              border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
              borderRadius: '16px',
              padding: '24px',
              boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
              boxSizing: 'border-box',
              textAlign: 'center',
            }}
          >
            <div
              style={{
                width: '56px',
                height: '56px',
                borderRadius: '50%',
                background: 'rgba(239, 68, 68, 0.12)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: '#ef4444',
                margin: '0 auto 16px auto',
              }}
            >
              <AlertCircle size={28} />
            </div>
            
            <h3 style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '18px', fontWeight: 800, margin: '0 0 10px 0' }}>
              تأكيد الحذف
            </h3>
            
            <p style={{ color: isLight ? '#475569' : '#94a3b8', fontSize: '14px', margin: '0 0 24px 0', lineHeight: 1.5 }}>
              {deleteConfirm.message}
            </p>
            
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
              <button
                onClick={async () => {
                  if (deleteConfirm.type === 'DEBT') {
                    await debtsService.deleteDebt(deleteConfirm.id);
                    await refreshData();
                    if (selectedDebt && selectedDebt.id === deleteConfirm.id) {
                      setIsDetailsModalOpen(false);
                    }
                  } else if (deleteConfirm.type === 'PAYMENT') {
                    await debtsService.deletePayment(deleteConfirm.id);
                    if (deleteConfirm.extraId) {
                      const updated = await debtsService.getDebt(deleteConfirm.extraId);
                      if (updated) setSelectedDebt(updated);
                    }
                    await refreshData();
                  }
                  setDeleteConfirm({ isOpen: false, type: 'DEBT', id: '', message: '' });
                }}
                style={{
                  flex: 1,
                  background: '#ef4444',
                  color: '#ffffff',
                  border: 'none',
                  borderRadius: '8px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = '#dc2626'}
                onMouseLeave={(e) => e.currentTarget.style.background = '#ef4444'}
              >
                حذف
              </button>
              <button
                onClick={() => setDeleteConfirm({ isOpen: false, type: 'DEBT', id: '', message: '' })}
                style={{
                  flex: 1,
                  background: isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)',
                  color: isLight ? '#475569' : '#e2e8f0',
                  border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
                  borderRadius: '8px',
                  padding: '10px 16px',
                  fontSize: '14px',
                  fontWeight: 700,
                  cursor: 'pointer',
                  transition: 'background 0.2s',
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = isLight ? '#e2e8f0' : 'rgba(255,255,255,0.1)'}
                onMouseLeave={(e) => e.currentTarget.style.background = isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)'}
              >
                إلغاء
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

// ==================== STYLED SUB-COMPONENTS ====================

const StatsCard: React.FC<{
  title: string;
  value: string | number;
  color: string;
  isLight: boolean;
  icon: React.ReactNode;
}> = ({ title, value, color, isLight, icon }) => (
  <div
    style={{
      background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
      border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
      borderRadius: '12px',
      padding: '20px',
      display: 'flex',
      alignItems: 'center',
      gap: '16px',
      boxShadow: isLight ? '0 4px 6px rgba(0,0,0,0.01)' : 'none',
      transition: 'transform 0.2s',
      cursor: 'default'
    }}
    onMouseEnter={e => {
      e.currentTarget.style.transform = 'translateY(-2px)';
    }}
    onMouseLeave={e => {
      e.currentTarget.style.transform = 'translateY(0)';
    }}
  >
    <div
      style={{
        width: '46px',
        height: '46px',
        borderRadius: '10px',
        background: `rgba(${color === '#3b82f6' ? '59,130,246' : color === '#10b981' ? '16,185,129' : color === '#f59e0b' ? '245,158,11' : color === '#ef4444' ? '239,68,68' : '99,102,241'}, 0.12)`,
        color: color,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0
      }}
    >
      {icon}
    </div>
    <div style={{ flex: 1, textAlign: 'right' }}>
      <div style={{ color: '#64748b', fontSize: '12px', fontWeight: 500, marginBottom: '4px' }}>{title}</div>
      <div style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '18px', fontWeight: 800 }}>{value}</div>
    </div>
  </div>
);

const AlertSectionCard: React.FC<{
  title: string;
  debts: Debt[];
  color: string;
  isLight: boolean;
  onView: (d: Debt) => void;
}> = ({ title, debts, color, isLight, onView }) => (
  <div
    style={{
      background: isLight ? '#ffffff' : 'rgba(13,31,60,0.7)',
      border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.08)'}`,
      borderRadius: '12px',
      padding: '20px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px'
    }}
  >
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', borderBottom: `1px solid ${isLight ? '#f1f5f9' : 'rgba(255,255,255,0.06)'}`, paddingBottom: '10px', marginBottom: '4px' }}>
      <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: color }} />
      <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 700, color: isLight ? '#0f172a' : '#f1f5f9' }}>
        {title} ({debts.length})
      </h3>
    </div>

    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', maxHeight: '200px', overflowY: 'auto' }}>
      {debts.length === 0 ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', color: '#10b981', fontSize: '12.5px', padding: '12px 6px' }}>
          <CheckCircle size={16} />
          <span>لا توجد ديون مستحقة ضمن هذا النطاق.</span>
        </div>
      ) : (
        debts.map(d => (
          <div
            key={d.id}
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '10px 12px',
              background: isLight ? '#f8fafc' : 'rgba(255,255,255,0.02)',
              borderRadius: '8px',
              border: `1px solid ${isLight ? '#e2e8f0' : 'rgba(255,255,255,0.05)'}`
            }}
          >
            <div>
              <div style={{ color: isLight ? '#1e293b' : '#f1f5f9', fontWeight: 600, fontSize: '12.5px' }}>{d.subscriber_name}</div>
              <div style={{ color: '#64748b', fontSize: '11px', marginTop: '2px' }}>
                المبلغ المتبقي: <span style={{ fontWeight: 600, color: '#f59e0b' }}>{d.remaining_amount.toLocaleString()} د.ع</span> | يستحق: {d.due_date}
              </div>
            </div>
            <button
              onClick={() => onView(d)}
              style={{
                background: 'rgba(59,130,246,0.1)',
                border: 'none',
                color: '#3b82f6',
                borderRadius: '6px',
                padding: '6px 12px',
                fontSize: '11px',
                fontWeight: 700,
                cursor: 'pointer'
              }}
            >
              تفاصيل
            </button>
          </div>
        ))
      )}
    </div>
  </div>
);

const ModalWrapper: React.FC<{
  title: string;
  isLight: boolean;
  onClose: () => void;
  width?: string;
  children: React.ReactNode;
}> = ({ title, isLight, onClose, width = '550px', children }) => (
  <div
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0,0,0,0.6)',
      backdropFilter: 'blur(4px)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
      boxSizing: 'border-box'
    }}
  >
    <div
      className="responsive-modal-card"
      style={{
        width: '100%',
        maxWidth: width,
        maxHeight: '90vh',
        overflowY: 'auto',
        background: isLight ? '#ffffff' : '#0a1628',
        border: `1px solid ${isLight ? '#cbd5e1' : 'rgba(255,255,255,0.1)'}`,
        borderRadius: '16px',
        padding: '24px',
        boxShadow: '0 20px 25px -5px rgba(0,0,0,0.3)',
        boxSizing: 'border-box',
        position: 'relative'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
        <h3 style={{ color: isLight ? '#0f172a' : '#f1f5f9', fontSize: '18px', fontWeight: 800, margin: 0 }}>
          {title}
        </h3>
        <button
          onClick={onClose}
          style={{
            border: 'none',
            background: 'transparent',
            color: isLight ? '#64748b' : '#94a3b8',
            cursor: 'pointer',
            padding: '4px'
          }}
        >
          <X size={18} />
        </button>
      </div>
      {children}
    </div>
  </div>
);

export default DebtsPage;
